import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  FormControl,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAppDispatch, useAppSelector } from '../../../store/hooks.ts';
import { selectCurrentOrganization } from '../../../store/organization/organizationSlice.ts';
import {
  addTmMitigation,
  removeTmMitigation,
  selectTmMitigations,
  setTmMitigations,
  updateTmMitigation,
} from '../store/tmMitigation/tmMitigationSlice.ts';
import {
  createTmMitigation,
  deleteTmMitigationById,
  listTmMitigations,
  updateTmMitigationById,
} from '../../../api.ts';
import { TmMitigation } from '../models/tmMitigation.ts';
import { selectSelectedTmAssessmentId } from '../store/tmAssessment/tmAssessmentSlice.ts';
import { useExcelTableState } from '../../../hooks/useExcelTableState.ts';
import {
  parsePasteData,
  calculateNextCell,
  applyPastedData,
  getRowBackgroundColor,
  getRowHoverColor,
} from '../../../utils/excelTableUtils.ts';

const mitigationStatuses = ['open', 'planned', 'in_progress', 'implemented'];

interface EditableMitigation {
  id?: number;
  description: string;
  status: string;
  priority: number;
  effort: number;
  isNew?: boolean;
  touched?: boolean;
}

const TmMitigationTable: React.FC = () => {
  const dispatch = useAppDispatch();
  const mitigations = useAppSelector(selectTmMitigations);
  const currentOrg = useAppSelector(selectCurrentOrganization);
  const selectedAssessmentId = useAppSelector(selectSelectedTmAssessmentId);
  const [editableRows, setEditableRows] = useState<EditableMitigation[]>([]);
  const [error, setError] = useState<string | null>(null);

  const {
    focusedRowIndex,
    editingCell,
    setEditingCell,
    handleRowFocus: baseHandleRowFocus,
    handleRowBlur: baseHandleRowBlur,
    isSaving,
    markSaving,
    markSaved,
  } = useExcelTableState<EditableMitigation>();

  const canEdit = currentOrg?.role && currentOrg.role !== 'viewer';
  const assessmentMissing = !selectedAssessmentId;

  // Define field order for Tab navigation and paste
  const fieldOrder: (keyof EditableMitigation)[] = ['description', 'status', 'priority', 'effort'];

  const loadMitigations = useCallback(async () => {
    if (!selectedAssessmentId) {
      dispatch(setTmMitigations([]));
      setError(null);
      return;
    }

    try {
      setError(null);
      const data = await listTmMitigations(selectedAssessmentId);
      dispatch(setTmMitigations(data));
    } catch (err: any) {
      console.error('Failed to load mitigations', err);
      setError(err?.response?.data?.detail || 'Failed to load mitigations');
    }
  }, [dispatch, selectedAssessmentId]);

  useEffect(() => {
    loadMitigations();
  }, [loadMitigations]);

  // Create rows with empty row at end
  const rows = useMemo(() => {
    const existingRows: EditableMitigation[] = mitigations.map(m => ({
      id: m.id,
      description: m.description,
      status: m.status,
      priority: m.priority,
      effort: m.effort,
    }));

    // Always add empty row for new entries
    const emptyRow: EditableMitigation = {
      description: '',
      status: 'open',
      priority: 0,
      effort: 0,
      isNew: true,
    };

    return [...existingRows, emptyRow];
  }, [mitigations]);

  useEffect(() => {
    setEditableRows(rows);
  }, [rows]);

  // Helper to determine if a cell should be highlighted as required
  const isRequiredFieldEmpty = (row: EditableMitigation, field: keyof EditableMitigation): boolean => {
    const requiredFields: (keyof EditableMitigation)[] = ['description'];
    if (!requiredFields.includes(field)) return false;

    const value = row[field];
    return value === '' || value === null || value === undefined;
  };

  // Helper to get cell background color with smart highlighting
  const getCellBackgroundColor = (row: EditableMitigation, field: keyof EditableMitigation): string => {
    // Don't highlight untouched new rows
    if (row.isNew && !row.touched) {
      return 'transparent';
    }

    if (isRequiredFieldEmpty(row, field)) {
      return 'linear-gradient(135deg, rgba(255, 193, 7, 0.12), rgba(255, 160, 0, 0.08))';
    }
    return 'transparent';
  };

  const updateRow = (index: number, field: keyof EditableMitigation, value: any) => {
    setEditableRows(prev => {
      const updated = [...prev];
      const row = updated[index];
      updated[index] = {
        ...row,
        [field]: value,
        ...(row.isNew && !row.touched ? { touched: true } : {}),
      };
      return updated;
    });
  };

  const saveRow = useCallback(
    async (row: EditableMitigation, index: number) => {
      if (!selectedAssessmentId) return;

      const rowKey = row.id ?? `new-${index}`;

      // Prevent duplicate saves
      if (isSaving(rowKey)) return;

      try {
        if (row.isNew) {
          // Create only if description is filled
          if (row.description.trim()) {
            markSaving(rowKey);
            const created = await createTmMitigation({
              description: row.description.trim(),
              status: row.status,
              priority: row.priority,
              effort: row.effort,
              assessment_id: selectedAssessmentId,
            });
            dispatch(addTmMitigation(created));
          }
        } else if (row.id) {
          // Update existing
          if (row.description.trim()) {
            markSaving(rowKey);
            const payload = {
              description: row.description.trim(),
              status: row.status,
              priority: row.priority,
              effort: row.effort,
              assessment_id: selectedAssessmentId,
            };
            const updated = await updateTmMitigationById(row.id, payload);
            dispatch(updateTmMitigation(updated));
          }
        }
      } catch (err: any) {
        console.error('Failed to save mitigation', err);
        setError(err?.response?.data?.detail || 'Failed to save mitigation');
      } finally {
        markSaved(rowKey);
      }
    },
    [dispatch, selectedAssessmentId, isSaving, markSaving, markSaved]
  );

  const handleRowFocus = (index: number) => {
    baseHandleRowFocus(index, editableRows, saveRow);
  };

  const handleRowBlur = () => {
    baseHandleRowBlur(editableRows, saveRow);
  };

  const handleTabNavigation = (
    e: React.KeyboardEvent,
    currentRow: number,
    currentField: keyof EditableMitigation
  ) => {
    if (e.key !== 'Tab') return;

    e.preventDefault();

    const currentFieldIndex = fieldOrder.indexOf(currentField);
    const nextCell = calculateNextCell(
      currentRow,
      currentFieldIndex,
      editableRows.length,
      fieldOrder.length,
      e.shiftKey
    );

    if (nextCell) {
      if (nextCell.row !== currentRow) {
        handleRowFocus(nextCell.row);
      }
      setEditingCell({ row: nextCell.row, field: fieldOrder[nextCell.fieldIndex] });
    }
  };

  const handlePaste = useCallback(
    (e: React.ClipboardEvent, startRowIndex: number, startField: keyof EditableMitigation) => {
      e.preventDefault();
      const pasteData = e.clipboardData.getData('text');
      const startFieldIndex = fieldOrder.indexOf(startField);

      // Parse the pasted data
      const parsedRows = parsePasteData(pasteData);

      // Normalize parsed data
      const normalizedRows = parsedRows.map(row =>
        row.map(cell => {
          const trimmed = cell.trim();
          if (
            trimmed === '—' ||
            trimmed === '-' ||
            trimmed === '–' ||
            trimmed === '−' ||
            trimmed === 'N/A' ||
            trimmed === 'n/a'
          ) {
            return '';
          }
          return cell;
        })
      );

      // Create empty row function
      const createEmptyRow = (): EditableMitigation => ({
        description: '',
        status: 'open',
        priority: 0,
        effort: 0,
        isNew: true,
        touched: true,
      });

      setEditableRows(prev => {
        const updated = applyPastedData(
          prev,
          normalizedRows,
          startRowIndex,
          startFieldIndex,
          fieldOrder,
          new Map(), // No related entities for this table
          createEmptyRow
        );
        // Mark affected rows as touched
        return updated.map((row, idx) => {
          if (idx >= startRowIndex && idx < startRowIndex + normalizedRows.length && row.isNew) {
            return { ...row, touched: true };
          }
          return row;
        });
      });

      // Auto-save pasted rows after a short delay
      setTimeout(() => {
        normalizedRows.forEach((_, rowOffset) => {
          const targetRowIndex = startRowIndex + rowOffset;
          if (targetRowIndex < editableRows.length) {
            saveRow(editableRows[targetRowIndex], targetRowIndex);
          }
        });
      }, 300);
    },
    [editableRows, saveRow, fieldOrder]
  );

  const handleDelete = async (mitigation: EditableMitigation) => {
    if (!mitigation.id) return;
    if (!window.confirm(`Delete mitigation "${mitigation.description}"?`)) return;

    try {
      setError(null);
      await deleteTmMitigationById(mitigation.id);
      dispatch(removeTmMitigation(mitigation.id));
    } catch (err: any) {
      console.error('Failed to delete mitigation', err);
      setError(err?.response?.data?.detail || 'Failed to delete mitigation');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Mitigations</Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {assessmentMissing && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Select or create an assessment to view and edit mitigations.
        </Alert>
      )}

      <TableContainer component={Paper} sx={{ border: '1px solid', borderColor: 'divider' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell
                sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText' }}
              >
                Description
              </TableCell>
              <TableCell
                sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText', width: 150 }}
              >
                Status
              </TableCell>
              <TableCell
                sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText', width: 100 }}
              >
                Priority
              </TableCell>
              <TableCell
                sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText', width: 100 }}
              >
                Effort
              </TableCell>
              <TableCell
                align="right"
                sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'primary.contrastText', width: 60 }}
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {editableRows.map((row, index) => {
              const isFocused = focusedRowIndex === index;
              const isEvenRow = index % 2 === 0;

              return (
                <TableRow
                  key={row.id ?? `new-${index}`}
                  sx={{
                    bgcolor: getRowBackgroundColor(isFocused, row.isNew || false, isEvenRow),
                    '&:hover': {
                      bgcolor: getRowHoverColor(isFocused),
                    },
                    transition: 'background-color 0.15s ease',
                  }}
                >
                  {/* Description */}
                  <TableCell>
                    {editingCell?.row === index && editingCell?.field === 'description' ? (
                      <TextField
                        value={row.description}
                        onChange={e => updateRow(index, 'description', e.target.value)}
                        onBlur={() => {
                          handleRowBlur();
                          setEditingCell(null);
                        }}
                        onKeyDown={e => handleTabNavigation(e, index, 'description')}
                        onPaste={e => handlePaste(e, index, 'description')}
                        size="small"
                        fullWidth
                        multiline
                        variant="standard"
                        placeholder={row.isNew ? 'Enter description...' : ''}
                        InputProps={{ disableUnderline: true }}
                        sx={{ '& textarea': { p: 1 } }}
                        autoFocus
                        disabled={!canEdit}
                      />
                    ) : (
                      <div
                        onClick={() => {
                          if (canEdit) {
                            handleRowFocus(index);
                            setEditingCell({ row: index, field: 'description' });
                          }
                        }}
                        style={{
                          padding: '8px',
                          minHeight: '20px',
                          cursor: canEdit ? 'text' : 'default',
                          userSelect: 'text',
                          background: getCellBackgroundColor(row, 'description'),
                          transition: 'background 0.15s ease',
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        {row.description || (row.isNew ? <span style={{ color: '#999' }}>Enter description...</span> : '')}
                      </div>
                    )}
                  </TableCell>

                  {/* Status */}
                  <TableCell>
                    {editingCell?.row === index && editingCell?.field === 'status' ? (
                      <FormControl fullWidth size="small" variant="standard">
                        <Select
                          value={row.status}
                          onChange={(e: SelectChangeEvent<string>) => {
                            updateRow(index, 'status', e.target.value);
                          }}
                          onBlur={() => {
                            handleRowBlur();
                            setEditingCell(null);
                          }}
                          onKeyDown={e => handleTabNavigation(e, index, 'status')}
                          disableUnderline
                          sx={{ '& .MuiSelect-select': { p: 1 } }}
                          autoFocus
                          disabled={!canEdit}
                        >
                          {mitigationStatuses.map(status => (
                            <MenuItem key={status} value={status}>
                              {status.replace(/_/g, ' ')}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <div
                        onClick={() => {
                          if (canEdit) {
                            handleRowFocus(index);
                            setEditingCell({ row: index, field: 'status' });
                          }
                        }}
                        style={{
                          padding: '8px',
                          minHeight: '20px',
                          cursor: canEdit ? 'pointer' : 'default',
                          userSelect: 'text',
                          background: getCellBackgroundColor(row, 'status'),
                          transition: 'background 0.15s ease',
                        }}
                      >
                        {row.status.replace(/_/g, ' ')}
                      </div>
                    )}
                  </TableCell>

                  {/* Priority */}
                  <TableCell>
                    {editingCell?.row === index && editingCell?.field === 'priority' ? (
                      <TextField
                        type="number"
                        value={row.priority}
                        onChange={e => updateRow(index, 'priority', Number(e.target.value))}
                        onBlur={() => {
                          handleRowBlur();
                          setEditingCell(null);
                        }}
                        onKeyDown={e => handleTabNavigation(e, index, 'priority')}
                        onPaste={e => handlePaste(e, index, 'priority')}
                        size="small"
                        fullWidth
                        variant="standard"
                        InputProps={{ disableUnderline: true }}
                        inputProps={{ min: 0, max: 5 }}
                        sx={{ '& input': { p: 1 } }}
                        autoFocus
                        disabled={!canEdit}
                      />
                    ) : (
                      <div
                        onClick={() => {
                          if (canEdit) {
                            handleRowFocus(index);
                            setEditingCell({ row: index, field: 'priority' });
                          }
                        }}
                        style={{
                          padding: '8px',
                          minHeight: '20px',
                          cursor: canEdit ? 'text' : 'default',
                          userSelect: 'text',
                          background: getCellBackgroundColor(row, 'priority'),
                          transition: 'background 0.15s ease',
                        }}
                      >
                        {row.priority}
                      </div>
                    )}
                  </TableCell>

                  {/* Effort */}
                  <TableCell>
                    {editingCell?.row === index && editingCell?.field === 'effort' ? (
                      <TextField
                        type="number"
                        value={row.effort}
                        onChange={e => updateRow(index, 'effort', Number(e.target.value))}
                        onBlur={() => {
                          handleRowBlur();
                          setEditingCell(null);
                        }}
                        onKeyDown={e => handleTabNavigation(e, index, 'effort')}
                        onPaste={e => handlePaste(e, index, 'effort')}
                        size="small"
                        fullWidth
                        variant="standard"
                        InputProps={{ disableUnderline: true }}
                        inputProps={{ min: 0, max: 5 }}
                        sx={{ '& input': { p: 1 } }}
                        autoFocus
                        disabled={!canEdit}
                      />
                    ) : (
                      <div
                        onClick={() => {
                          if (canEdit) {
                            handleRowFocus(index);
                            setEditingCell({ row: index, field: 'effort' });
                          }
                        }}
                        style={{
                          padding: '8px',
                          minHeight: '20px',
                          cursor: canEdit ? 'text' : 'default',
                          userSelect: 'text',
                          background: getCellBackgroundColor(row, 'effort'),
                          transition: 'background 0.15s ease',
                        }}
                      >
                        {row.effort}
                      </div>
                    )}
                  </TableCell>

                  {/* Actions */}
                  <TableCell align="right">
                    {!row.isNew && (
                      <IconButton
                        aria-label="delete mitigation"
                        onClick={() => handleDelete(row)}
                        disabled={!canEdit}
                        size="small"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default TmMitigationTable;
